/**
 * CSRF-aware API service
 */
import axios from 'axios';
import { getApiUrl, logConfig } from '../config/apiConfig';

// APIのベースURL
const baseURL = getApiUrl();
const finalBaseURL = baseURL.endsWith('/api') 
  ? baseURL
  : baseURL.endsWith('/') 
    ? `${baseURL}api` 
    : `${baseURL}/api`;

// CSRF token storage
let csrfToken = null;
let csrfTokenPromise = null;

// CSRF token management
const csrfManager = {
  // Get CSRF token from server
  async getToken() {
    if (csrfTokenPromise) {
      return csrfTokenPromise;
    }

    csrfTokenPromise = axios.get(`${finalBaseURL}/csrf-token`)
      .then(response => {
        csrfToken = response.data.csrfToken;
        if (logConfig.enableApiLogging) {
          console.log('[CSRF] Token obtained successfully');
        }
        return csrfToken;
      })
      .catch(error => {
        console.error('[CSRF] Failed to get token:', error);
        csrfToken = null;
        throw error;
      })
      .finally(() => {
        csrfTokenPromise = null;
      });

    return csrfTokenPromise;
  },

  // Clear token (on logout or token expiry)
  clearToken() {
    csrfToken = null;
    csrfTokenPromise = null;
  },

  // Get current token
  getCurrentToken() {
    return csrfToken;
  }
};

// Enhanced Axios instance with CSRF protection
export const csrfApi = axios.create({
  baseURL: finalBaseURL,
  timeout: 600000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for authentication and CSRF
csrfApi.interceptors.request.use(
  async (config) => {
    // Add authentication token
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    // Add CSRF token for state-changing methods
    if (['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase())) {
      try {
        // Get CSRF token if we don't have one
        if (!csrfToken) {
          csrfToken = await csrfManager.getToken();
        }
        
        if (csrfToken) {
          config.headers['X-CSRFToken'] = csrfToken;
        }
      } catch (error) {
        console.error('[CSRF] Failed to get token for request:', error);
        // Continue without CSRF token in development mode
        if (process.env.NODE_ENV === 'production') {
          throw error;
        }
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
csrfApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle authentication errors
    if (error.response?.status === 401) {
      if (window.location.pathname !== '/login') {
        localStorage.removeItem('auth_token');
        csrfManager.clearToken();
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    // Handle CSRF errors
    if (error.response?.status === 403 && 
        error.response?.data?.error?.message?.includes('CSRF')) {
      
      if (logConfig.enableApiLogging) {
        console.log('[CSRF] Token invalid, refreshing...');
      }

      // Clear invalid token and retry once
      if (!originalRequest._retryCount) {
        originalRequest._retryCount = 1;
        csrfManager.clearToken();
        
        try {
          csrfToken = await csrfManager.getToken();
          if (csrfToken) {
            originalRequest.headers['X-CSRFToken'] = csrfToken;
            return csrfApi.request(originalRequest);
          }
        } catch (retryError) {
          console.error('[CSRF] Retry failed:', retryError);
        }
      }
    }

    return Promise.reject(error);
  }
);

// Security-aware API methods
export const secureApi = {
  // Initialize CSRF token
  async initialize() {
    try {
      csrfToken = await csrfManager.getToken();
      if (logConfig.enableApiLogging) {
        console.log('[API] Security initialized');
      }
    } catch (error) {
      console.error('[API] Security initialization failed:', error);
      // Don't throw in development mode
      if (process.env.NODE_ENV === 'production') {
        throw error;
      }
    }
  },

  // Clear security tokens
  clearTokens() {
    csrfManager.clearToken();
    localStorage.removeItem('auth_token');
  },

  // Get CSRF token status
  getCSRFStatus() {
    return {
      hasToken: !!csrfToken,
      token: csrfToken ? `${csrfToken.substring(0, 8)}...` : null
    };
  }
};

// Enhanced project API with CSRF protection
export const secureProjectApi = {
  // Read operations (no CSRF needed)
  getProjects: () => csrfApi.get('/projects/'),
  getProject: (projectId) => csrfApi.get(`/projects/${projectId}`),
  getOcrResults: (projectId) => csrfApi.get(`/projects/${projectId}/results`),
  getOcrResult: (projectId, resultId) => csrfApi.get(`/projects/${projectId}/results/${resultId}`),

  // Write operations (CSRF protected)
  createProject: (projectData) => csrfApi.post('/projects/', projectData),
  updateProject: (projectId, projectData) => csrfApi.put(`/projects/${projectId}/`, projectData),
  deleteProject: (projectId) => csrfApi.delete(`/projects/${projectId}/`),

  // File upload with CSRF protection
  processFile: async (projectId, formData) => {
    // Ensure we have a CSRF token before file upload
    if (!csrfToken) {
      csrfToken = await csrfManager.getToken();
    }

    return csrfApi.post(`/projects/${projectId}/ocr`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'X-CSRFToken': csrfToken
      },
    });
  },

  // Generic methods
  get: (path) => csrfApi.get(`/projects${path}`),
  post: (path, data) => csrfApi.post(`/projects${path}`, data),
  put: (path, data) => csrfApi.put(`/projects${path}`, data),
  delete: (path) => csrfApi.delete(`/projects${path}`),
};

// Enhanced user API with CSRF protection
export const secureUserApi = {
  // Read operations
  getProfile: () => csrfApi.get('/users/me'),
  
  // Write operations (CSRF protected)
  updateProfile: (userData) => csrfApi.put('/users/me', userData),
  changePassword: (passwordData) => csrfApi.post('/users/change-password', passwordData),
  verifyEmail: (token) => csrfApi.post('/users/verify-email', { token }),
  requestPasswordReset: (email) => csrfApi.post('/users/reset-password-request', { email }),
  resetPassword: (token, newPassword) => csrfApi.post('/users/reset-password', { 
    token, 
    new_password: newPassword 
  }),
};

// Authentication API with CSRF protection
export const secureAuthApi = {
  // Login (get CSRF token first)
  async login(credentials) {
    // Get CSRF token before login
    csrfToken = await csrfManager.getToken();

    // Convert to form data for OAuth2PasswordRequestForm
    const formData = new URLSearchParams();
    formData.append('username', credentials.username);
    formData.append('password', credentials.password);

    return csrfApi.post('/auth/login', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-CSRFToken': csrfToken
      }
    });
  },

  // Register with CSRF protection
  async register(userData) {
    // Get CSRF token before registration
    csrfToken = await csrfManager.getToken();
    
    return csrfApi.post('/auth/register', userData);
  },

  // Logout
  async logout() {
    try {
      await csrfApi.post('/auth/logout');
    } finally {
      // Clear tokens regardless of response
      secureApi.clearTokens();
    }
  }
};

// Compatibility layer - export original API structure but with security
export const api = csrfApi;
export const projectApi = secureProjectApi;
export const userApi = secureUserApi;

// Export CSRF manager for direct access if needed
export { csrfManager };

// Log configuration
if (logConfig.enableApiLogging) {
  console.log('[API] CSRF-aware API service initialized');
  console.log('[API] Base URL:', finalBaseURL);
}
